import React, { useEffect, useState } from 'react';
import { FaTimes, FaRoute, FaMapMarkedAlt, FaSpinner } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { tripsAPI } from '../api/api';
import '../css/ViewTripModal.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom start marker (green)
const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 12.5 12.5 28.5 12.5 28.5S25 25 25 12.5C25 5.596 19.404 0 12.5 0z" fill="#10b981" stroke="white" stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="5" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41]
});

// Custom end marker (checkered flag)
const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
      <line x1="4" y1="4" x2="4" y2="38" stroke="#333" stroke-width="3" stroke-linecap="round"/>
      <rect x="6" y="4" width="22" height="16" fill="white" stroke="#333" stroke-width="1"/>
      <rect x="6" y="4" width="5.5" height="4" fill="#333"/>
      <rect x="17" y="4" width="5.5" height="4" fill="#333"/>
      <rect x="11.5" y="8" width="5.5" height="4" fill="#333"/>
      <rect x="22.5" y="8" width="5.5" height="4" fill="#333"/>
      <rect x="6" y="12" width="5.5" height="4" fill="#333"/>
      <rect x="17" y="12" width="5.5" height="4" fill="#333"/>
      <rect x="11.5" y="16" width="5.5" height="4" fill="#333"/>
      <rect x="22.5" y="16" width="5.5" height="4" fill="#333"/>
    </svg>
  `),
  iconSize: [32, 40],
  iconAnchor: [4, 40],
  popupAnchor: [12, -40]
});

// Component to fit map bounds to route
const FitBounds = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions && positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [positions, map]);

  return null;
};

const ViewTripModal = ({ open, onClose, trip }) => {
  const [tripDetails, setTripDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && trip?.id) {
      fetchTripDetails();
    } else {
      setTripDetails(null);
      setError(null);
    }
  }, [open, trip?.id]);

  const fetchTripDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await tripsAPI.getTripDetails(trip.id);
      setTripDetails(details);
    } catch (err) {
      console.error('Error fetching trip details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !trip) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDistance = (distance) => {
    if (!distance) return '-';
    return `${parseFloat(distance).toFixed(2)} km`;
  };

  // Use detailed data if available, otherwise fall back to basic trip data
  const displayTrip = tripDetails || trip;
  const gpsData = tripDetails?.route_points || tripDetails?.gps_data || [];

  // Koordináták a térképhez
  const hasStartCoords = displayTrip.start_latitude && displayTrip.start_longitude;
  const hasEndCoords = displayTrip.end_latitude && displayTrip.end_longitude;
  const startPosition = hasStartCoords ? [parseFloat(displayTrip.start_latitude), parseFloat(displayTrip.start_longitude)] : null;
  const endPosition = hasEndCoords ? [parseFloat(displayTrip.end_latitude), parseFloat(displayTrip.end_longitude)] : null;

  // Build route from GPS data points
  let routePositions = [];
  if (gpsData.length > 0) {
    // Use all GPS points for the route
    routePositions = gpsData.map(point => [
      parseFloat(point.latitude || point.lat),
      parseFloat(point.longitude || point.lng || point.lon)
    ]).filter(pos => !isNaN(pos[0]) && !isNaN(pos[1]));
  } else if (startPosition && endPosition) {
    // Fallback to direct line if no GPS data
    routePositions = [startPosition, endPosition];
  }

  // Térkép középpont
  const mapCenter = routePositions.length > 0
    ? routePositions[Math.floor(routePositions.length / 2)]
    : startPosition || endPosition || [47.4979, 19.0402];

  return (
    <div className="view-modal-overlay" onClick={onClose}>
      <div className="view-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="view-modal-header">
          <h2>Út #{trip.id}</h2>
          <button className="view-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="view-modal-body">
          {loading ? (
            <div className="view-modal-loading">
              <FaSpinner className="spinner" />
              <span>Adatok betöltése...</span>
            </div>
          ) : error ? (
            <div className="view-modal-error">
              <p>Hiba történt az adatok betöltésekor: {error}</p>
            </div>
          ) : (
            <>
              <div className="view-modal-grid">
                {/* Bal oldal - Út információk */}
                <div className="view-modal-section">
                  <h3><FaRoute /> Út információk</h3>
                  <table className="view-info-table">
                    <tbody>
                      <tr>
                        <th>VIN</th>
                        <td>{displayTrip.vehicle_vin || displayTrip.chassis_number17 || '-'}</td>
                      </tr>
                      <tr>
                        <th>Típus</th>
                        <td>
                          <span className={`trip-type-badge ${displayTrip.trip_type}`}>
                            {displayTrip.trip_type_label || (displayTrip.trip_type === 'business' ? 'Üzleti' : 'Magán')}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th>Távolság</th>
                        <td>{formatDistance(displayTrip.distance)}</td>
                      </tr>
                      <tr>
                        <th>Kiindulási pont</th>
                        <td>
                          {hasStartCoords ? (
                            <>
                              <strong>{parseFloat(displayTrip.start_latitude).toFixed(6)}, {parseFloat(displayTrip.start_longitude).toFixed(6)}</strong>
                              {displayTrip.start_address && <div className="address-text">{displayTrip.start_address}</div>}
                            </>
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <th>Úticél</th>
                        <td>
                          {hasEndCoords ? (
                            <>
                              <strong>{parseFloat(displayTrip.end_latitude).toFixed(6)}, {parseFloat(displayTrip.end_longitude).toFixed(6)}</strong>
                              {displayTrip.end_address && <div className="address-text">{displayTrip.end_address}</div>}
                            </>
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <th>Kezdete</th>
                        <td>{formatDate(displayTrip.start_time)}</td>
                      </tr>
                      <tr>
                        <th>Vége</th>
                        <td>{formatDate(displayTrip.end_time)}</td>
                      </tr>
                      <tr>
                        <th>Leírás</th>
                        <td>{displayTrip.description || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Jobb oldal - Térkép */}
                <div className="view-modal-section">
                  <h3><FaMapMarkedAlt /> Útvonal térkép</h3>
                  <div className="view-map-container">
                    {(hasStartCoords || hasEndCoords || routePositions.length > 0) ? (
                      <MapContainer
                        center={mapCenter}
                        zoom={14}
                        style={{ height: '100%', width: '100%', borderRadius: '8px' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />

                        {/* Fit map to route bounds */}
                        {routePositions.length > 0 && (
                          <FitBounds positions={routePositions} />
                        )}

                        {/* Start marker */}
                        {startPosition && (
                          <Marker position={startPosition} icon={startIcon}>
                            <Popup>
                              <strong>Kiindulási pont</strong><br />
                              {displayTrip.start_address || `${startPosition[0].toFixed(6)}, ${startPosition[1].toFixed(6)}`}
                            </Popup>
                          </Marker>
                        )}

                        {/* End marker */}
                        {endPosition && (
                          <Marker position={endPosition} icon={endIcon}>
                            <Popup>
                              <strong>Úticél</strong><br />
                              {displayTrip.end_address || `${endPosition[0].toFixed(6)}, ${endPosition[1].toFixed(6)}`}
                            </Popup>
                          </Marker>
                        )}

                        {/* Route line from GPS points */}
                        {routePositions.length >= 2 && (
                          <Polyline
                            positions={routePositions}
                            color="#3b82f6"
                            weight={4}
                            opacity={0.8}
                          />
                        )}
                      </MapContainer>
                    ) : (
                      <div className="no-map-data">
                        Nincs elérhető koordináta adat
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </>
          )}
        </div>

        <div className="view-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Bezárás
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewTripModal;
